export default {
  // Page titles
  title: 'Properties',
  new_property: 'New Property',
  edit_property: 'Edit Property',

  // Descriptions
  description: 'Manage your rental properties',
  new_description: 'Add a new rental property',
  edit_description: 'Update property details',

  // Tabs
  tabs: {
    overview: 'Overview',
    units: 'Units',
    general: 'General',
    rooms: 'Rooms',
    pricing: 'Pricing',
    common_area_fees: 'Common Area Fees',
    parking: 'Parking',
    microsite: 'Microsite',
  },

  // Section titles
  sections: {
    basic_information: 'Basic Information',
    billing_settings: 'Billing Settings',
    billing_settings_hint: 'Utility rates (electricity, water, etc.) are configured on the Pricing tab after the property is saved.',
    // EP-136 (T136-026) — Booking block, sits directly below Billing Settings.
    booking: 'Booking',
    payment_methods: 'Payment Methods',
    utility_rates: 'Utility Rates & Pricing Formula',
    utility_rates_description: 'Configure utility rates and additional fees for this property. These will be used when generating invoices.',
    // EP-86 T010: Essentials/Advanced section grouping
    essentials: 'Essentials',
    contact: 'Contact',
    tax: 'Tax',
    location: 'Location',
    advanced_options: 'Advanced options',
    // EP-86 T014: PropertyPricingFields section headings
    pricing_formula: 'Pricing Formula',
    common_area_fees: 'Common Area Fees',
    // EP-86 T015: PropertyRoomsFields streamlined mode section headings
    area_meters: 'Area & Meters',
    room_types: 'Room Types',
  },

  // Rooms (streamlined mode quick-add)
  rooms: {
    add_rooms: 'Add your rooms',
    unit_number: 'Room number',
    // F10/AC-8 (EP-158): was the hardcoded "e.g., APT-101".
    unit_number_placeholder: 'e.g. 101',
    monthly_rent: 'Monthly rent (฿)',
    add_unit_button: '+ Add',
    added_count_one: '{{count}} room added',
    added_count_other: '{{count}} rooms added',
    streamlined_empty_title: 'No rooms yet',
    streamlined_empty_hint: 'Enter a room number and monthly rent above, then tap "+ Add".',
    streamlined_number_required: 'Enter a room number',
    streamlined_rent_required: 'Enter the monthly rent',
    streamlined_add_required: 'Add at least one room. Fill in the fields above and tap "+ Add".',
    // AddUnitModal: Advanced room-type grouping disclosure
    group_into_room_types: 'Advanced: group into room types',
  },

  // Property types
  property_types: {
    apartment: 'Apartment',
    condominium: 'Condominium',
  },

  // Labels
  labels: {
    property_name: 'Property Name',
    property_type: 'Property Type',
    address: 'Address',
    billing_day: 'Billing Day',
    billing_day_option: 'Day {{day}}',
    // EP-149 T149-017 (MON-8): nullable, separate from billing_day above.
    payment_due_day: 'Payment Due Day',
    payment_due_day_option: 'Day {{day}}',
    payment_due_day_blank: 'Issue date + 15 days',
    deposit_type: 'Deposit Type',
    deposit_value_months: 'Number of Months',
    deposit_value_fixed: 'Deposit Amount (฿)',
    pricing_formula: 'Pricing Formula',
    room_types: 'Room Types',
    units: 'Units',
    total_units: 'Total Units',
    vacant_units: 'Vacant Units',
    occupied_units: 'Occupied Units',
    // EP-136 T136-020 — units held by a paid/billed booking fee, distinct
    // from both occupied and vacant.
    reserved_units: 'Reserved Units',
    tax_id: 'Tax ID',
    branch_number: 'Branch Number',
    phone: 'Phone',
    email: 'Email',
    payment_methods_override: 'Override account payment settings',
    using_account_defaults: 'Using account default settings',
    location_url: 'Location',
    view_on_map: 'View on Map',
  },

  // Placeholders
  placeholders: {
    property_name: 'e.g., Peerapat Residence',
    address: 'e.g., 123 Main Street',
    tax_id: 'e.g., 1234567890123',
    branch_number: '00000 for head office',
    phone: 'e.g., 02-123-4567',
    email: 'e.g., info@property.com',
    location_url: 'https://maps.google.com/...',
    bank_name_example: 'Bangkok Bank',
    account_number_example: '123-4-56789-0',
    account_name_example: 'Peerapat Residence',
  },

  // Deposit types
  deposit_types: {
    months: 'Months of rent',
    fixed: 'Fixed amount (฿)',
  },

  // EP-136 (T136-026) — per-property booking settings, falling back to the
  // account-level defaults in Settings PER KEY (Property#resolved_booking_settings).
  booking: {
    description: 'Each setting below can inherit the account-wide default (set in Settings → Account) or be overridden just for this property.',
    fee_mode_label: 'Booking Fee Treatment',
    fee_default_label: 'Default Booking Amount',
    forfeit_label: 'Forfeit booking fee on cancellation',
    fee_mode_options: {
      deposit: 'Deposit — counts toward the deposit, refundable',
      revenue: 'Revenue — non-refundable reservation fee',
    },
    override_toggle_label: 'Override for this property',
    overridden_badge: 'Overridden',
    inherited_badge: 'Inherited',
    inherited_value: 'Account default: {{value}}',
    fee_default_not_set: 'No default set — the amount must be entered every time a unit is booked',
    forfeit_forfeited: 'Forfeited on cancellation',
    forfeit_refunded: 'Refunded on cancellation',
  },

  // Validation
  validation: {
    name_required: 'Property name is required',
    must_be_positive: 'Must be positive',
  },

  // Room types
  room_types: {
    title: 'Room Types',
    add: 'Add Room Type',
    name: 'Room Type Name',
    name_placeholder: 'e.g., Studio, 1BR, 2BR',
    floor: 'Floor',
    floor_placeholder: 'e.g., 1, G, B1',
    empty: 'No room types',
    empty_description: 'Add a room type to start organizing your units.',
    import_spreadsheet: 'Import from spreadsheet',
    prefill_pending: 'pending from import, preview only',
    // EP-54: i18next v25 plural forms for the prefill banner count.
    count_one: '{{count}} room type',
    count_other: '{{count}} room types',
    importing_units_one: 'Importing {{count}} unit…',
    importing_units_other: 'Importing {{count}} units…',
    imported_units_one: '✓ {{count}} unit imported. Click Next to move on.',
    imported_units_other: '✓ {{count}} units imported. Click Next to move on.',
  },

  // Pricing formula
  pricing: {
    title: 'Pricing Formula',
    add_item: 'Add Item',
    item_name: 'Item Name',
    item_type: 'Type',
    rate: 'Rate',
    unit: 'Unit',
    amount: 'Amount',
    minimum: 'Minimum',
    types: {
      metered: 'Metered',
      fixed: 'Fixed',
      percentage: 'Percentage',
    },
  },

  // PricingFormulaEditor — all user-facing strings (task-108)
  pricing_editor: {
    presets: {
      rent: { name: 'Rent' },
      water: { name: 'Water' },
      electricity: { name: 'Electricity' },
      common_fee: { name: 'Common Fee', name_long: 'Common Area Fee' },
      internet: { name: 'Internet' },
      garbage: { name: 'Garbage' },
      parking: { name: 'Parking' },
    },
    types: {
      metered: 'Per Usage',
      fixed: 'Fixed Amount',
      percentage: 'Percentage of Total',
      per_sqm: 'Per m² (Area)',
      per_share_ratio: 'Per Share Ratio',
    },
    units: {
      unit: 'Unit',
      kwh: 'kWh',
      cubic_meter: 'Cubic Meter (m³)',
    },
    // Accessible names for ฿ / % inputs (task-107B)
    aria: {
      rate: '{{name}} rate',
      minimum: '{{name}} minimum',
      amount: '{{name}} amount',
      percentage: '{{name}} percentage',
      per_sqm: '{{name}} rate per square metre',
      per_share_ratio: '{{name}} base amount × unit share ratio',
      type: '{{name}} type',
      unit: '{{name}} unit',
      // EP-114 T014/T015 (S2-11) — the stacked move-up/move-down buttons were
      // replaced by ONE drag handle, so their labels are gone and this single
      // handle label carries the item's position in the list instead.
      drag_handle: 'Reorder {{name}}, item {{position}} of {{total}}',
      remove: 'Remove {{name}}',
    },
    // EP-114 T015 (S2-11) — screen-reader copy for the drag-and-drop
    // reordering. Rendered by @dnd-kit into its own aria-live region
    // (announcements) and its hidden instructions element.
    dnd: {
      role_description: 'sortable pricing item',
      instructions:
        'To reorder a pricing item, press space or enter on its reorder button to pick it up, use the up and down arrow keys to move it, then press space or enter again to drop it in its new position. Press escape to cancel.',
      picked_up: 'Picked up {{name}}. It is item {{position}} of {{total}}.',
      moved_over: '{{name}} was moved to position {{position}} of {{total}}.',
      dropped: '{{name}} was dropped at position {{position}} of {{total}}.',
      cancelled: 'Reordering was cancelled. {{name}} stays at position {{position}} of {{total}}.',
    },
    placeholders: {
      minimum: 'Min (optional)',
    },
    empty_state: 'No pricing items configured. Add utilities below.',
    cta: {
      add_defaults: 'Add typical Thai apartment defaults',
      add_custom: 'Add custom item',
      pick_preset: 'Pick from presets',
    },
    quick_add: 'Quick add:',
    add_custom: 'Add Custom Item',
    add_item_button: 'Add Item',
    custom_item_label: 'Custom Item Name',
    custom_item_placeholder: 'e.g., Cable TV, Laundry',
    of_total: 'of total',
    per_month: 'per month',
    per_sqm_unit: '× unit m²',
    per_share_ratio_unit: '× unit share ratio',
    minimum_suffix: '(min {{amount}})',
  },

  // Bulk readings
  bulk_readings: {
    title: 'Bulk Meter Readings',
    description: 'Record meter readings for all units',
  },

  // Empty states
  empty: {
    title: 'No properties',
    description: 'Get started by creating your first property.',
  },

  // Setup guide
  setup_guide: {
    title: 'Welcome to Properties',
    description:
      'Properties are the foundation of FlowRent. Start by creating your first property, then add room types, units, and meters.',
    step_1_title: 'Create your property',
    step_1_description: 'Add your apartment or building with address and billing settings.',
    step_2_title: 'Define room types',
    step_2_description: 'Set up categories like Studio, 1BR, 2BR with floor information.',
    step_3_title: 'Add individual units',
    step_3_description: 'Create rooms within each room type with pricing details.',
    step_4_title: 'Configure meters',
    step_4_description: 'Set up water and electricity meters for each unit.',
    import: 'Import',
    import_off_scope_warning: 'Found {{count}} {{entity}} rows. Import them from the {{Entity}} page.',
    download_sample: 'Download sample',
    seed_sample: 'Seed sample data',
    seed_sample_confirm: 'This will create: 1 property + 1 room type + 1 unit. Continue?',
    seed_sample_success: 'Sample property (with room type and unit) created successfully.',
    seed_sample_noop: 'You already have properties. Sample data was not added.',
  },

  // Late fee policy
  late_fee_policy: {
    title: 'Late Fee Policy',
    enable_label: 'Automatically apply late fees to overdue invoices',
    fee_type_label: 'Fee Type',
    fee_type_fixed: 'Fixed Amount',
    fee_type_percentage: 'Percentage',
    amount_label: 'Amount',
    amount_hint_percentage: 'Calculated on unpaid balance',
    grace_period_label: 'Grace Period (days)',
    grace_period_hint: 'Days after due date before late fee applies. 0 = apply from day 1 after due date.',
    disable_warning: 'Disabling does not remove already-applied fees. Edit the invoice to remove them.',
    save_button: 'Save Late Fee Policy',
    save_success: 'Late fee policy saved.',
    save_error: 'Failed to save. Please check the form.',
    unit_override: 'Override for this unit',
    use_property_default: 'Use property default',
    clear_override: 'Clear Override',
  },

  // Explainer cards
  explainer: {
    pricing: {
      body: 'Set the rates tenants pay for utilities and recurring fees. The sample bill on the right updates as you edit.',
      details: 'The pricing formula drives every invoice line item. A correct formula means tenants see a predictable bill and you spend less time answering queries. The sample bill uses typical consumption (10 units water, 50 units electricity) so you can sanity-check the numbers before saving.',
    },
    late_fee: {
      body: 'Automatically charge a penalty when tenants pay after the due date.',
      details: 'Late fees create a financial incentive for on-time payment without manual follow-up. The grace period lets you absorb a few days of delay before the penalty kicks in. Disabling does not remove already-applied fees. Edit the invoice to remove those.',
    },
  },

  // Actions
  actions: {
    add_property: 'Add Property',
    import_ai: 'Import with AI',
    create_property: 'Create property',
    view_readings: 'Bulk Readings',
    // P007: building-level reports deep-link affordance
    view_building_reports: 'Building Reports',
    view_building_pl: 'Profit & Loss',
    view_building_overdue: 'Overdue Invoices',
  },

  // Messages
  messages: {
    delete_title: 'Delete Property',
    delete_confirm: 'Delete "{{name}}"? This will also delete all room types and units.',
    created: 'Property created successfully',
    updated: 'Property updated successfully',
    deleted: 'Property deleted successfully',
    unsaved_changes_title: 'Unsaved Changes',
    unsaved_changes_body: 'You have unsaved changes. Are you sure you want to leave?',
  },

  // Errors (Zod validation messages)
  errors: {
    name_required: 'Property name is required',
    invalid_email: 'Invalid email address',
    invalid_url: 'Invalid URL',
  },

  // PricingPreview component strings
  pricing_preview: {
    breakdown_aria_label: 'Pricing breakdown',
    aria_label: 'Sample bill preview',
    title: 'Sample bill',
    mobile_summary: 'Sample bill total: {{total}}',
    loading: 'Computing preview…',
    error: 'Preview unavailable',
    // F10/AC-8 (EP-158): the subtitle used to join raw object keys
    // ("water"/"electricity") straight in. Kept as plain English units here
    // (the mirror of the Thai fix), never raw keys.
    water_label: 'water',
    electricity_label: 'electricity',
    consumption_item: '{{value}} units {{label}}',
    estimate_note:
      'Estimate at sample usage. Actual invoices use recorded meter readings and add any common-area fees, so the final total will differ.',
  },

  // EP-56 L3 — multi-line formula transparency block (audit panel)
  formula_transparency: {
    title: 'How is this computed?',
    show: 'Show',
    hide: 'Hide',
  },
}
